import React from "react";
import {
  Button,
  Col,
  Divider,
  Form,
  Row,
  Space,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  DownloadOutlined,
  LinkOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import CustomInput from "../../shared/form/reactHookForm/CustomInput";
import CustomCheckbox from "../../shared/form/reactHookForm/CustomCheckbox";
import CustomDatePicker from "../../shared/form/reactHookForm/CustomDatePicker";
import CustomInputTextArea from "../../shared/form/reactHookForm/CustomInputTextArea";
import { uploadContractAction, downloadContractAction } from "../../../store/company/companyActions";
import { dateFormat } from "../../shared/formatting";

const { Text } = Typography;

const leftLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const rightLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const CompanyContractPanel = ({ control }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.company.data);
  const uploading = useSelector((state) => state.company.uploading);
  const companyKey = useSelector(
    (state) => state.actor?.details?.data?.BrokerId,
  );

  const handleUploadContract = (info) => {
    dispatch(uploadContractAction(info.file, companyKey));
  };

  const handleDownloadContract = () => {
    if (data?.ContractFileName) {
      dispatch(downloadContractAction(companyKey, data.ContractFileName));
    }
  };

  return (
    <Form size="small" layout="horizontal">
      {/* ── Signatory ── */}
      <Divider orientation="left" plain style={{ marginTop: 0 }}>
        Signatory
      </Divider>
      <Row gutter={32}>
        <Col span={12}>
          <CustomInput
            control={control}
            layout={leftLayout}
            formLabel="Name"
            name="SignatoryName"
          />
          <CustomInput
            control={control}
            layout={leftLayout}
            formLabel="Title"
            name="SignatoryTitle"
          />
        </Col>
        <Col span={12}>
          <CustomInput
            control={control}
            layout={rightLayout}
            formLabel="Email"
            name="SignatoryEmail"
          />
          <CustomInput
            control={control}
            layout={rightLayout}
            formLabel="Phone"
            name="SignatoryPhone"
          />
        </Col>
      </Row>

      {/* ── Contract ── */}
      <Divider orientation="left" plain>
        Contract
      </Divider>
      <Row gutter={32}>
        <Col span={12}>
          <CustomDatePicker
            formItem={{
              layout: leftLayout,
              label: "Signed Date",
            }}
            control={control}
            name="ContractSignedDate"
            format={dateFormat}
            allowClear={true}
            timeOfDay="start"
          />
          <CustomInput
            control={control}
            layout={leftLayout}
            formLabel="DocuSign Link"
            name="ContractDocuSignLink"
          />
          <CustomInput
            control={control}
            layout={leftLayout}
            formLabel="DocuSign Envelope ID"
            name="ContractDocuSignEnvelopeId"
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            label="Contract File"
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Space wrap>
                <Upload 
                  accept=".pdf"
                  showUploadList={false}
                  maxCount={1}
                  customRequest={handleUploadContract}
                >
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    loading={uploading}
                  >
                    Upload PDF
                  </Button>
                </Upload>
                {data?.ContractFileName && (
                  <Button
                    size="small"
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadContract}
                  >
                    {data.ContractFileName}
                  </Button>
                )}
              </Space>
              {data?.ContractUploadedBy && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Uploaded by {data.ContractUploadedBy}
                  {data.ContractUploadedDate &&
                    ` on ${dayjs(data.ContractUploadedDate).format("YYYY-MM-DD HH:mm")}`}
                </Text>
              )}
            </Space>
          </Form.Item>
        </Col>
      </Row>

      {/* ── MK154A ── */}
      <Divider orientation="left" plain>
        MK154A Authorization
      </Divider>
      <Row gutter={32}>
        <Col span={12}>
          <CustomCheckbox
            control={control}
            layout={leftLayout}
            formLabel="Submitted"
            name="MK154AIsSubmitted"
          >
            Yes
          </CustomCheckbox>
          <CustomDatePicker
            formItem={{
              layout: leftLayout,
              label: "Confirmed Date",
            }}
            control={control}
            name="MK154AConfirmedDate"
            format={dateFormat}
            allowClear={true}
            timeOfDay="start"
          />
        </Col>
        <Col span={12}>
          <CustomInputTextArea
            control={control}
            layout={rightLayout}
            formLabel="Notes"
            name="MK154ANotes"
            style={{ resize: "none", height: 70 }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default CompanyContractPanel;